import { PlusOutlined } from "@ant-design/icons";
import {
  Divider,
  Empty,
  Select,
  Spin,
  type EmptyProps,
  type SelectProps,
} from "antd";
import classNames from "classnames";
import { debounce, groupBy, unionBy } from "lodash";
import { useCallback, useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { useEffectOnce, useUpdateEffect } from "react-use";

import { unaccentSearch } from "../../lib/formats/common";
import type { QueryLazyOptions } from "../../lib/graphql";
import { findCellWithInput } from "./InputNumber";

export interface SelectOption {
  key: string | number | boolean;
  selectValue?: string | number | boolean;
  label: ReactNode;
  group?: string;
  children?: ReactNode;
  [key: string]: any;
}

interface NewItemProps {
  entityName: ReactNode;
  onClick(): void;
}

interface OptionsHookReturnType {
  load?: (options?: QueryLazyOptions<any>) => void;
  search?: (value?: string) => void;
  options?: any[];
  loading?: boolean;
}

export interface SelectFieldProps extends Omit<
  SelectProps<any>,
  "options" | "onChange" | "onSearch" | "filterOption" | "optionFilterProp"
> {
  showGroups?: boolean;
  groupsSorter?(a: string, b: string): number;
  showSearch?: boolean;
  onSearch?: (value: string) => void;
  filterOption?: boolean | ((inputValue: string, option?: any) => boolean);
  optionFilterProp?: string;
  defaultValues?: SelectOption[] | null;
  emptyProps?: EmptyProps;
  newItemProps?: NewItemProps;
  options?: SelectOption[] | null | undefined;
  labelRenderer?: (label: ReactNode, option: SelectOption) => ReactNode;
  optionsHook?: (params?: any) => OptionsHookReturnType;
  optionsHookFilter?: (option: any) => boolean;
  onChange?: (value: any, options: any) => void;
  optionsHookParams?: any;
  formatOption?: (content: ReactNode, option: any) => ReactNode;
  refetchOnOpen?: boolean;
  enableArrowNavigation?: boolean;
  loadValueOnChange?: boolean;
  preload?: boolean;
}

const optionValue = (option: SelectOption) => option.selectValue ?? option.key;

function withPopupClassName(
  selectClassNames: SelectProps<any>["classNames"]
): SelectProps<any>["classNames"] {
  if (typeof selectClassNames === "function") return selectClassNames;

  const popup = selectClassNames?.popup;

  return {
    ...selectClassNames,
    popup: {
      ...(typeof popup === "object" ? popup : undefined),
      root: classNames(
        "notranslate",
        typeof popup === "string" ? popup : popup?.root
      ),
    },
  };
}

function getSearchConfig(
  showSearch: boolean,
  searchFunc: ((value: string) => void) | undefined,
  debouncedSearch: ((value: string) => void) | undefined,
  fetchDefaultOptions: () => void,
  filterOption: SelectFieldProps["filterOption"],
  optionFilterProp: string
): SelectProps<any>["showSearch"] {
  if (!showSearch) return false;

  return {
    onSearch: debouncedSearch
      ? (value) =>
          value === "" ? fetchDefaultOptions() : debouncedSearch(value)
      : undefined,
    filterOption:
      filterOption ??
      ((input, option) =>
        !searchFunc && option
          ? unaccentSearch(input, option[optionFilterProp])
          : true),
    optionFilterProp,
  };
}

export function SelectField({
  onSearch,
  loading,
  options,
  defaultValues,
  showGroups,
  groupsSorter = (a, b) => a.localeCompare(b),
  showSearch = true,
  newItemProps,
  emptyProps,
  onChange,
  labelRenderer,
  optionsHook = () => ({}),
  optionsHookFilter = () => true,
  optionsHookParams,
  formatOption = (content) => content,
  refetchOnOpen,
  className,
  optionFilterProp = "label",
  filterOption,
  enableArrowNavigation = false,
  onKeyDown,
  loadValueOnChange = false,
  classNames: selectClassNames,
  popupRender,
  onOpenChange,
  preload = false,
  ...restProps
}: SelectFieldProps) {
  const {
    load,
    search,
    options: hookOptions = [],
    loading: hookLoading,
  } = optionsHook(optionsHookParams);
  const [defaultOptionsFetched, setDefaultOptionsFetched] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dataLoading = loading || hookLoading;

  const searchFunc = onSearch || search;
  const debouncedSearch = searchFunc && debounce(searchFunc, 500);

  const opts = unionBy(hookOptions, options, defaultValues, "key").filter(
    optionsHookFilter
  );
  const defaultValue = defaultValues && defaultValues.map(optionValue);

  const fetchDefaultOptions = useCallback(() => {
    setDefaultOptionsFetched(true);

    // provide any variables to refetch data from network instead of fetching from cache
    // https://github.com/apollographql/apollo-client/blob/main/src/core/ObservableQuery.ts#L809
    if (load)
      load(optionsHookParams || { variables: { filter: { id: undefined } } });
    else if (searchFunc) searchFunc("");
  }, [load, optionsHookParams, searchFunc]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<any>) => {
      const navigationKeys = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
      ];
      if (navigationKeys.includes(e.key)) {
        if (isDropdownOpen && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
          return;
        }

        // Find the Select's hidden input (not the search input)
        const selectElement = e.currentTarget;

        const cell = selectElement.closest("td");
        const row = cell?.closest("tr");

        if (cell && row) {
          let targetCell: HTMLTableCellElement | null = null;
          const cellIndex = Array.from(row.cells).indexOf(
            cell as HTMLTableCellElement
          );

          // Prevent dropdown from opening on Left/Right navigation
          e.preventDefault();
          e.stopPropagation();

          switch (e.key) {
            case "ArrowDown":
            case "ArrowUp":
              targetCell = findCellWithInput(
                cell as HTMLTableCellElement,
                (c) => {
                  const currentRow = c.closest("tr");
                  const nextRow =
                    e.key === "ArrowDown"
                      ? (currentRow?.nextElementSibling as HTMLTableRowElement)
                      : (currentRow?.previousElementSibling as HTMLTableRowElement);
                  return nextRow?.cells[cellIndex] || null;
                }
              );
              break;
            case "ArrowRight":
            case "ArrowLeft":
              targetCell = findCellWithInput(
                cell as HTMLTableCellElement,
                (c) =>
                  e.key === "ArrowRight"
                    ? (c.nextElementSibling as HTMLTableCellElement)
                    : (c.previousElementSibling as HTMLTableCellElement)
              );
              break;
          }

          if (targetCell) {
            const nextInput = targetCell.querySelector("input");
            if (nextInput) {
              // Set navigating flag to prevent dropdown from opening
              setIsNavigating(true);

              nextInput.focus();
              nextInput.select();

              // Reset flag after a short delay
              setTimeout(() => setIsNavigating(false), 100);
            }
          }
        }
      }

      onKeyDown?.(e);
    },
    [onKeyDown, isDropdownOpen]
  );

  // preload selected options on render if value is present and no default value provided
  useEffectOnce(() => {
    if (
      preload ||
      !restProps.value ||
      restProps.labelInValue ||
      defaultValue ||
      !load ||
      (Array.isArray(restProps.value) && !restProps.value.length)
    ) {
      return;
    }

    load({ variables: { filter: { id: restProps.value } } });
  });

  useEffectOnce(() => {
    if (preload && !defaultOptionsFetched) fetchDefaultOptions();
  });

  useUpdateEffect(() => {
    if (!loadValueOnChange || !restProps.value || !load) return;

    if (opts.find((o) => optionValue(o) === restProps.value)) return;

    load({ variables: { filter: { id: restProps.value } } });
  }, [restProps.value]);

  // reload if optionHookParams are changed
  useUpdateEffect(() => {
    if (defaultOptionsFetched) fetchDefaultOptions();
  }, [optionsHookParams]);

  const searchConfig = getSearchConfig(
    showSearch,
    searchFunc,
    debouncedSearch,
    fetchDefaultOptions,
    filterOption,
    optionFilterProp
  );

  return (
    <Select
      allowClear={true}
      open={isNavigating ? false : undefined}
      notFoundContent={
        dataLoading ? (
          <Spin size="small" />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} {...emptyProps} />
        )
      }
      loading={dataLoading}
      showSearch={searchConfig}
      defaultValue={restProps.mode && !defaultValue ? [] : defaultValue}
      optionLabelProp="label"
      labelRender={
        labelRenderer
          ? ({ label, value }) => {
              const option = opts.find((o) => optionValue(o) === value);
              return option ? labelRenderer(label, option) : label;
            }
          : undefined
      }
      popupRender={
        popupRender ||
        (newItemProps
          ? (menu) => (
              <SelectField.NewDropdown menu={menu} props={newItemProps} />
            )
          : undefined)
      }
      onChange={(val) =>
        onChange &&
        onChange(val != undefined ? val : restProps.mode ? [] : null, opts)
      }
      onOpenChange={(open) => {
        // Track dropdown state for keyboard navigation
        setIsDropdownOpen(open);

        // load default options on open when not loaded
        // and when mode is set to `tags` or `multiple` (ex: WO employees selector)
        if ((open && !defaultOptionsFetched) || restProps.mode || refetchOnOpen)
          fetchDefaultOptions();
        onOpenChange?.(open);
      }}
      onClear={fetchDefaultOptions}
      onKeyDown={enableArrowNavigation ? handleKeyDown : onKeyDown}
      className={classNames("notranslate", className)}
      classNames={withPopupClassName(selectClassNames)}
      {...restProps}
    >
      {showGroups &&
        Object.entries(groupBy(opts, "group"))
          .toSorted(([a], [b]) => groupsSorter(a, b))
          .map(([group, opts]) => (
            <Select.OptGroup key={group} label={group}>
              {opts.map((o) => (
                <Select.Option
                  key={o.key}
                  value={optionValue(o)}
                  label={o.label}
                  disabled={o.disabled}
                >
                  {formatOption(o.children || o.label, o)}
                </Select.Option>
              ))}
            </Select.OptGroup>
          ))}
      {!showGroups &&
        opts.map((o) => (
          <Select.Option
            key={o.key}
            value={optionValue(o)}
            disabled={o.disabled}
            label={o.label}
          >
            {formatOption(o.children || o.label, o)}
          </Select.Option>
        ))}
    </Select>
  );
}

SelectField.NewDropdown = ({
  menu,
  props: { entityName, onClick },
}: {
  menu: ReactNode;
  props: NewItemProps;
}) => {
  return (
    <SelectField.DropdownFooter menu={menu} onClick={onClick}>
      <PlusOutlined />{" "}
      <FormattedMessage id="new.header" values={{ entityName }} />
    </SelectField.DropdownFooter>
  );
};

SelectField.DropdownFooter = ({
  menu,
  onClick,
  mode = "footer",
  children,
}: {
  menu: ReactNode;
  onClick: () => void;
  mode?: "footer" | "header";
  children: ReactNode;
}) => {
  const content = (
    <div
      className="select-new-item"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </div>
  );

  const [top, bottom] = mode === "footer" ? [menu, content] : [content, menu];

  return (
    <>
      {top}
      <Divider style={{ margin: "4px 0" }} />
      {bottom}
    </>
  );
};
